
// region JS Imports

// region External Modules
import Promise from "bluebird"

import I18n from "plugins/i18n-js"
// endregion External Modules

// region Private Modules
import {
  captureException,
} from "app/error_reporting"
import {
  q_ajax_3,
  ERROR_HANDLER_ACTIONS as AJAX_ERROR_HANDLER_ACTIONS,
} from "app/utils/q_ajax/q_ajax_3"
// endregion Private Modules

// region Public Components
// endregion Public Components

// region Private Components
// endregion Private Components

// endregion JS Imports


interface ResourceAttributes {
  requester_name?: string | null,

  requester_email_address: string,

  requester_phone_number: string,
  requester_phone_number_country_code?: string | null,

  additional_message?: string | null,

  anticipated_checkin_date?: string | null,
  stay_duration_in_days?: string | null,
}

interface Input {
  user_authentication_handle: string,
  listing_id: string | number,
  listing_contact_request_attributes: ResourceAttributes,
  reject_promotions?: boolean | null,
}

/* eslint-disable no-unused-vars */
enum ResultTypeEnums {
  SUCCESS =                               "SUCCESS",
  UNAUTHENTICATED =                       "UNAUTHENTICATED",
  UNAUTHORIZED =                          "UNAUTHORIZED",
  INVALID =                               "INVALID",
  LISTING_EXPIRED =                       "LISTING_EXPIRED",
  CONFLICTING_RESOURCE_ALREADY_EXISTS =   "CONFLICTING_RESOURCE_ALREADY_EXISTS",
  LISTING_CONTACT_EMAIL_OR_PHONE_ABSENT = "LISTING_CONTACT_EMAIL_OR_PHONE_ABSENT",
  INVALID_CONTACT_PHONE_NUMBER =          "INVALID_CONTACT_PHONE_NUMBER",
  SERVER_ERROR =                          "SERVER_ERROR",
  TOO_MANY_REQUEST =                      "TOO_MANY_REQUEST",
}
/* eslint-enable no-unused-vars */

interface DataResult {
  listing_contact_request_id: number | string,
}

interface PromiseResolvePayload {
  result_type: ResultTypeEnums,
  data?: DataResult,
}

const create = ({user_authentication_handle, listing_id, listing_contact_request_attributes, reject_promotions}: Input): Promise<PromiseResolvePayload> => {
  return Promise.resolve(
    import(
      "plugins/js-routes"
    )
  )
  .then((Routes) => {
    return q_ajax_3(
      {
        url:  Routes.create_api_web_app_listing_contact_requests_path(),
        type: "POST",

        // Expect JSON in response
        dataType: "JSON",

        // Sending JSON to server
        contentType:  "application/json; charset=utf-8",
        data:         JSON.stringify({
          user_authentication_handle:         user_authentication_handle,
          listing_id:                         listing_id,
          listing_contact_request_attributes: listing_contact_request_attributes,
          locale:                             I18n.locale,
          reject_promotions:                  reject_promotions,
        }),
      },
      {
        custom_error_handler: ({http_status_code}) => {
          switch (http_status_code) {
            case 401:
            case 403:
            case 422:
            case 429:
              return AJAX_ERROR_HANDLER_ACTIONS.STOP_RETRY
            default:
              return AJAX_ERROR_HANDLER_ACTIONS.CONTINUE_RETRY
          }
        },
      },
    )
  })
  .then(({data}) => {
    return (
      {
        result_type: ResultTypeEnums.SUCCESS,
        data: {
          listing_contact_request_id: data.data.listing_contact_request_id,
        },
      }
    )
  })
  .catch((reason_or_error) => {
    if (reason_or_error instanceof Error) {
      throw reason_or_error
    }

    const { response } = reason_or_error
    switch (response.status) {
      case 500:
      case 502:
      case 503:
      case 504:
        return ({result_type: ResultTypeEnums.SERVER_ERROR})
      case 401:
        return ({result_type: ResultTypeEnums.UNAUTHENTICATED})
      case 403:
        return ({result_type: ResultTypeEnums.UNAUTHORIZED})
      case 422:
        return response.json()
        .then((responsePayload: any) => {
          if (responsePayload.data.error_type === "listing_contact_email_or_phone_absent") {
            return ({
              result_type: ResultTypeEnums.LISTING_CONTACT_EMAIL_OR_PHONE_ABSENT,
            })
          }

          if (responsePayload.data.error_type === "invalid_contact_phone_number") {
            return ({
              result_type: ResultTypeEnums.INVALID_CONTACT_PHONE_NUMBER,
            })
          }

          if (responsePayload.data.error_type === "conflicting_resource_already_exists") {
            return ({
              result_type: ResultTypeEnums.CONFLICTING_RESOURCE_ALREADY_EXISTS,
            })
          }

          if (responsePayload.data.error_type === "listing_expired") {
            return ({
              result_type: ResultTypeEnums.LISTING_EXPIRED,
            })
          }

          return ({
            result_type: ResultTypeEnums.INVALID,
          })
        })
        .catch((e: any) => {
          // Got error parsing = just report as invalid
          captureException(
            e,
            {
              error_source:
                "app/resources/listing_contact_requests/create/index.es6",
            }
          )
          return ({result_type: ResultTypeEnums.INVALID})
        })
      case 429:
        return ({result_type: ResultTypeEnums.TOO_MANY_REQUEST})
      default:
        return ({result_type: ResultTypeEnums.INVALID})
    }
  })
}

export { create, ResultTypeEnums }
